import {create} from 'zustand';
import {Visit} from '../models/visit.model';

interface VisitsUIState {
  // Tab Management
  activeTab: 'list' | 'analytics';
  setActiveTab: (tab: 'list' | 'analytics') => void;

  // Modal States
  isDetailsModalOpen: boolean;
  selectedVisit: Visit | null;
  openDetailsModal: (visit: Visit) => void;
  closeDetailsModal: () => void;

  // Action Error States (for approve/reject errors)
  actionError: string | null;
  actionErrorDetails: { product_id?: number; current_quantity?: number; reported_quantity?: number } | null;
  setActionError: (error: string | null, details?: { product_id?: number; current_quantity?: number; reported_quantity?: number } | null) => void;
  clearActionError: () => void;

  // Action Loading — aligned with payments (actionLoading per action)
  actionLoading: { approve: boolean; reject: boolean };
  setActionLoading: (loading: Partial<{ approve: boolean; reject: boolean }>) => void;

  // Analytics Filters
  selectedPeriod: 'daily' | 'weekly' | 'monthly';
  selectedStore: number | undefined;
  dateFrom: string;
  dateTo: string;

  // Analytics Filter Actions
  setSelectedPeriod: (period: 'daily' | 'weekly' | 'monthly') => void;
  setSelectedStore: (storeId: number | undefined) => void;
  setDateFrom: (date: string) => void;
  setDateTo: (date: string) => void;
  clearAnalyticsFilters: () => void;

  // Store Visits State
  lastVisit: Visit | null;
  visitHistory: Visit[];
  storeVisitsLoading: boolean;
  storeVisitsError: string | null;

  // Store Visits Actions
  setLastVisit: (visit: Visit | null) => void;
  setVisitHistory: (visits: Visit[]) => void;
  setStoreVisitsLoading: (loading: boolean) => void;
  setStoreVisitsError: (error: string | null) => void;
  clearStoreVisits: () => void;

  // Utility Actions
  reset: () => void;
}

export const useVisitsUIStore = create<VisitsUIState>((set) => ({
  // Tab Management
  activeTab: 'list',
  setActiveTab: (tab) => set({activeTab: tab}),

  // Modal States
  isDetailsModalOpen: false,
  selectedVisit: null,
  openDetailsModal: (visit) => set({
    isDetailsModalOpen: true,
    selectedVisit: visit,
    actionError: null,
    actionErrorDetails: null
  }),
  closeDetailsModal: () => set({
    isDetailsModalOpen: false,
    selectedVisit: null,
    actionError: null,
    actionErrorDetails: null
  }),

  // Action Error States
  actionError: null,
  actionErrorDetails: null,
  setActionError: (error, details) => set({actionError: error, actionErrorDetails: details || null}),
  clearActionError: () => set({actionError: null, actionErrorDetails: null}),

  // Action Loading
  actionLoading: { approve: false, reject: false },
  setActionLoading: (loading) => set((s) => ({ actionLoading: { ...s.actionLoading, ...loading } })),

  // Analytics Filters
  selectedPeriod: 'monthly',
  selectedStore: undefined,
  dateFrom: '',
  dateTo: '',

  // Analytics Filter Actions
  setSelectedPeriod: (period) => set({selectedPeriod: period}),
  setSelectedStore: (storeId) => set({selectedStore: storeId}),
  setDateFrom: (date) => set({dateFrom: date}),
  setDateTo: (date) => set({dateTo: date}),
  clearAnalyticsFilters: () => set({
    selectedPeriod: 'monthly',
    selectedStore: undefined,
    dateFrom: '',
    dateTo: ''
  }),

  // Store Visits State
  lastVisit: null,
  visitHistory: [],
  storeVisitsLoading: false,
  storeVisitsError: null,

  // Store Visits Actions
  setLastVisit: (visit) => set({lastVisit: visit}),
  setVisitHistory: (visits) => set({visitHistory: visits}),
  setStoreVisitsLoading: (loading) => set({storeVisitsLoading: loading}),
  setStoreVisitsError: (error) => set({storeVisitsError: error}),
  clearStoreVisits: () => set({
    lastVisit: null,
    visitHistory: [],
    storeVisitsLoading: false,
    storeVisitsError: null
  }),

  // Utility Actions
  reset: () => set({
    activeTab: 'list',
    isDetailsModalOpen: false,
    selectedVisit: null,
    selectedPeriod: 'monthly',
    selectedStore: undefined,
    dateFrom: '',
    dateTo: '',
    lastVisit: null,
    visitHistory: [],
    storeVisitsLoading: false,
    storeVisitsError: null,
    actionError: null,
    actionErrorDetails: null,
    actionLoading: { approve: false, reject: false }
  })
}));
